import localStore from "store";
import dayjs from "dayjs";
import i18next from "i18next";
import sanitizeHtml from "sanitize-html";
import { MAX_FILE_SIZE, baseUrl, documentMimes, mediaMimes } from "./static";
import { v4 as uuidv4 } from "uuid";
import { SHA256 } from "crypto-js";
import {
  cloneDeep,
  escapeRegExp,
  forEach,
  includes,
  isEmpty,
  last,
  maxBy,
  replace,
  toUpper,
} from "lodash-es";

export const cleanBlankValue = (obj) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === null || obj[key] === undefined || obj[key] === "") {
      delete obj[key];
    }
  });

  return obj;
};

export const updateObject = (oldObject, updatedProperties) => {
  return {
    ...oldObject,
    ...updatedProperties,
  };
};

// ignoreInitEndpoint: abaikan endpoint perusahaan (app.endpoint) dan pakai base URL
// utama, mis. untuk endpoint autentikasi global seperti /authentication/refresh
// endpoint: pakai endpoint perusahaan ini alih-alih app.endpoint, mis. untuk
// inits di halaman pilih perusahaan (app.endpoint sengaja masih kosong)
export const generateBaseUri = ({
  ignoreInitEndpoint = false,
  endpoint = null,
} = {}) => {
  let apiUrl = baseUrl;
  let _baseUrl = apiUrl
    ? apiUrl.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "")
    : "";
  const hostname = window?.location?.hostname;
  const appEndpoint =
    endpoint || (ignoreInitEndpoint ? null : localStore.get("app.endpoint"));
  if (import.meta.env.PROD && !isEmpty(hostname)) {
    const splitHostname = hostname?.split(".");
    if (splitHostname[0] && appEndpoint) {
      _baseUrl = `${splitHostname[0]}.${_baseUrl}`;
    }
  }

  let protocol = includes(apiUrl, "https://") ? "https://" : "http://";
  if (includes("local.jokolodang.com", hostname)) {
    protocol = "http://";
  }
  if (appEndpoint) {
    apiUrl = `${protocol}${appEndpoint}/api/v1`;
  } else {
    apiUrl = `${protocol}${_baseUrl}`;
    if (includes("local.jokolodang.com", hostname)) {
      apiUrl = `http://app.kledo.test/api/v1`;
    } else if (includes("localssl.jokolodang.com", hostname)) {
      apiUrl = `https://app.kledo.test/api/v1`;
    }
  }

  return apiUrl;
};

export const getFormattedDate = (date) => {
  const dateToDayjs = dayjs(date);
  const now = dayjs();
  const yesterday = now.subtract(1, "day");

  if (dateToDayjs.isSame(now, "day")) {
    // Hari ini
    return dateToDayjs.format("HH:mm");
  } else if (dateToDayjs.isSame(yesterday, "day")) {
    // Kemarin
    return i18next.t("date.yesterday");
  } else {
    // Lebih dari kemarin
    return dateToDayjs.format("DD/MM/YY");
  }
};

export const sortById = (data) => {
  return data.sort((a, b) => {
    // if (isEmpty(a.messages?.id)) {
    //   return 0;
    // }
    const maxIdA = maxBy(a.messages, "id")?.id || 0;
    const maxIdB = maxBy(b.messages, "id")?.id || 0;
    return maxIdB - maxIdA;
  });
};

export const filterOption = (input, option) => {
  if (option.name) {
    return (
      option.name.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
    );
  } else if (option.label) {
    return (
      option.label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
    );
  } else if (option.children) {
    return (
      option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
    );
  }

  return true;
};

//generate card character by firstname and lastname
export const generateCardName = (name, moreChar) => {
  if (isEmpty(name)) {
    return "-";
  }
  const fullName = name?.split(" ");
  if (moreChar) {
    if (fullName.length > 2) {
      return toUpper(`${fullName[0].slice(0, 1)}${fullName[2].slice(0, 1)}`);
    } else if (fullName.length > 1) {
      return toUpper(`${fullName[0].slice(0, 1)}${fullName[1].slice(0, 1)}`);
    }
  }

  return toUpper(`${fullName[0].slice(0, 1)}`);
};

// Untuk menggabungkan array dicek berdasarkan id, sudah ada apa belum
export const mergedToUniqueById = (
  curentData = [],
  updateData = [],
  match = "id"
) => {
  if (!Array.isArray(curentData) || !Array.isArray(updateData)) {
    return [];
  }
  // Pakai Set (O(n+m)), JANGAN `.some` di dalam `.filter` (O(n·m)). Dipanggil
  // dari reducer dengan `state.messages` berupa draft Immer, jadi tiap akses
  // elemen lewat proxy trap — versi kuadratik memblokir main thread ~6 detik
  // saat merge history panjang (mis. conversation dari hasil search).
  const existingIds = new Set(curentData.map((row) => row[match]));
  return [
    ...curentData,
    ...updateData.filter((row) => !existingIds.has(row[match])),
  ];
};

// Untuk pengecekan tipe file yang disupport whatsapp
export const allowedFile = (mime, type = "media") => {
  let allowed = false;
  let mimes = [];
  if (type === "media") {
    mimes = mediaMimes;
  } else if (type === "document") {
    mimes = documentMimes;
  }
  if (includes(mimes, mime)) {
    allowed = true;
  }
  return allowed;
};

// Untuk pengecekan ukuran file dalam MB, parameter dalam kb
export const allowedFileSize = (size) => {
  return size / 1024 / 1024 <= MAX_FILE_SIZE;
};

export const replaceTemplateBody = ({ templateString = "", body = [] }) => {
  // Mengganti semua variabel dengan nilai yang sesuai,
  // untuk format body adalah array object dengan properties id dan value
  let replacedString = templateString;
  forEach(body, ({ id, value, color }) => {
    replacedString = replace(
      replacedString,
      new RegExp(escapeRegExp(`{{${id}}}`), "g"),
      `<span style="color: ${color};" class='font-bold'>${value}</span>`
    );
  });
  return replacedString;
};

export const generateEndpoint = (url) => `${generateBaseUri()}${url}`;

export const openNewTab = (moduleUrl = "", withBaseUri = true) => {
  const tempLink = document.createElement("a");
  tempLink.href = withBaseUri ? `${generateBaseUri()}${moduleUrl}` : moduleUrl;
  tempLink.setAttribute("target", "_blank");
  tempLink.click();
};

// Get base64 image
export const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

// Get dimension of image
export const getDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const img = document.createElement("img");
    img.onload = () => {
      resolve({
        height: img.naturalHeight,
        width: img.naturalWidth,
      });
    };
    img.onerror = reject;

    const reader = new FileReader();
    reader.onloadend = (ended) => {
      img.src = ended.target.result;
    };
    reader.readAsDataURL(file);
  });
};

export const convertFileSize = (bytes) => {
  const kiloBytes = bytes / 1024;
  const megaBytes = kiloBytes / 1024;

  if (megaBytes >= 1) {
    return `${megaBytes.toFixed(2)} MB`;
  } else if (kiloBytes >= 1) {
    return `${kiloBytes.toFixed(2)} KB`;
  } else {
    return `${bytes} Bytes`;
  }
};

export const getCapitalizedFileExtension = (filename) => {
  if (!filename) {
    return "";
  }
  const extension = filename?.split(".").pop(); // Mengambil ekstensi file
  const capitalizedExtension = extension.toUpperCase(); // Mengonversi ke huruf kapital
  return capitalizedExtension;
};

export const getNameFromUrl = (url) => {
  try {
    let name = "";
    if (url) {
      const pathname = new URL(url).pathname;
      if (pathname) {
        const splitPath = pathname?.split("/");
        name = splitPath ? decodeURI(last(splitPath)) : "";
      }
    }

    return name;
  } catch (error) {
    return url;
  }
};

export const getExtention = (filename) => {
  // To get the file extension
  const extension = filename.split(".").pop();
  if (extension.includes("?")) {
    return extension.split("?")?.[0];
  }
  return extension;
};

//gabungkan tailwind dengan pengondisian
export function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
//format ribuan
export function formatThousandSeparator(number) {
  if (!number) return 0;
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export const sortDate = (data) => {
  return data.sort(
    (prevDate, nextDate) =>
      new Date(prevDate).getTime() - new Date(nextDate).getTime()
  );
};

export const convertWhatsAppFormatToHTML = (text) => {
  if (typeof text !== "string") return "";
  // **bold** (BE / markdown style) - proses dulu agar tidak tertimpa single *
  text = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  text = text.replace(/\*(.*?)\*/g, "<strong>$1</strong>");
  text = text.replace(/_(.*?)_/g, "<em>$1</em>");
  text = text.replace(/~(.*?)~/g, "<s>$1</s>");
  text = text.replace(/```(.*?)```/g, "<code>$1</code>");
  text = text.replace(/\{\{(.*?)\}\}/g, (match, p1) => {
    return `<span class="variable">{{${p1 || "default"}}}</span>`;
  });
  text = text.replace(/\n/g, "<br/>"); // Handle new lines
  return text;
};

export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export const generateRandomId = () => {
  return Math.floor(Math.random() * (20000 - 10000 + 1)) + 10000;
};

export const generateMessageTime = () => {
  return dayjs().format("YYYY-MM-DD HH:mm:ss");
};

export const sanitizeMessage = (message) => {
  return sanitizeHtml(message, {
    allowedTags: [], // Disallow all HTML tags
    allowedAttributes: {}, // Disallow all attributes
    textFilter: function (text) {
      return text
        .replace(/&gt;/g, ">")
        .replace(/&lt;/g, "<")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&#x2F;/g, "/")
        .replace(/\t/g, "");
    },
  });
};

export const updateCacheInfiniteQuery = (
  queryClient,
  queryKey,
  itemId,
  updateFn = null
) => {
  let wasUpdated = false;

  queryClient.setQueryData(queryKey, (oldData) => {
    if (!oldData || !oldData.pages) return oldData;

    // Menciptakan salinan deep dari data
    const newData = cloneDeep(oldData);

    // Iterasi melalui setiap halaman
    newData.pages = newData.pages.map((page) => {
      // Jika halaman memiliki array data langsung
      if (Array.isArray(page.data)) {
        page.data = page.data.map((item) => {
          if (item.id === itemId) {
            wasUpdated = true;
            return updateFn ? updateFn(item) : item;
          }
          return item;
        });
      }

      // Jika halaman memiliki data.items array
      if (page.data?.items && Array.isArray(page.data.items)) {
        page.data.items = page.data.items.map((item) => {
          if (item.id === itemId) {
            wasUpdated = true;
            return updateFn ? updateFn(item) : item;
          }
          return item;
        });
      }

      return page;
    });

    return wasUpdated ? newData : oldData;
  });

  return wasUpdated;
};

export const addItemToCacheFirstPosition = (queryClient, queryKey, newItem) => {
  let wasAdded = false;

  queryClient.setQueryData(queryKey, (oldData) => {
    // Jika tidak ada data sebelumnya, tidak bisa menambahkan
    if (!oldData || !oldData.pages || oldData.pages.length === 0)
      return oldData;

    // Clone data yang sudah ada
    const newData = cloneDeep(oldData);

    // Ambil halaman pertama
    const firstPage = newData.pages[0];

    // Tambahkan item baru ke awal array data pada halaman pertama
    if (Array.isArray(firstPage.data)) {
      firstPage.data.unshift(newItem);
      wasAdded = true;
    }
    // Jika struktur datanya berbeda (contoh: { data: { items: [] } })
    else if (firstPage.data?.items && Array.isArray(firstPage.data.items)) {
      firstPage.data.items.unshift(newItem);
      wasAdded = true;
    }

    return wasAdded ? newData : oldData;
  });

  return wasAdded;
};

export const removeItemFromCache = (queryClient, queryKey, itemId) => {
  let wasRemoved = false;

  queryClient.setQueryData(queryKey, (oldData) => {
    // Jika tidak ada data sebelumnya, tidak bisa menghapus
    if (!oldData || !oldData.pages || oldData.pages.length === 0)
      return oldData;

    // Clone data yang sudah ada
    const newData = cloneDeep(oldData);

    // Iterasi melalui setiap halaman untuk mencari dan menghapus item
    newData.pages = newData.pages.map((page) => {
      // Jika halaman memiliki array data langsung
      if (Array.isArray(page.data)) {
        const initialLength = page.data.length;
        page.data = page.data.filter((item) => item.id !== itemId);
        if (initialLength > page.data.length) {
          wasRemoved = true;
        }
      }
      // Jika struktur datanya berbeda (contoh: { data: { items: [] } })
      else if (page.data?.items && Array.isArray(page.data.items)) {
        const initialLength = page.data.items.length;
        page.data.items = page.data.items.filter((item) => item.id !== itemId);
        if (initialLength > page.data.items.length) {
          wasRemoved = true;
        }
      }

      // // Perbarui total count jika ada
      // if (wasRemoved && page.meta && typeof page.meta.total === "number") {
      //   page.meta.total -= 1;
      // }

      return page;
    });

    return wasRemoved ? newData : oldData;
  });

  return wasRemoved;
};

export const compareWithJSON = (prevProps, nextProps) => {
  try {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps);
  } catch {
    return true;
  }
};

export function highlightSearchInHTML(htmlString, query) {
  if (typeof htmlString !== "string") return htmlString;
  if (!query) return htmlString;

  const escaped = query.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");

  return htmlString.replace(regex, "<mark>$1</mark>");
}

export function timeToMinutes(timeStr) {
  const time = dayjs(timeStr, "HH:mm:ss");
  return time.hour() * 60 + time.minute();
}

export function getOrCreateDeviceToken() {
  const key = "device_token";
  let token = localStore.get(key);

  if (!token) {
    const raw = `${uuidv4()}${Date.now()}`;
    const hash = SHA256(raw).toString(); // full SHA256
    token = hash.substring(0, 32); // boleh dipotong, misal 32 karakter
    localStore.set(key, token);
  }

  return token;
}

// shuffle array of number
export function shuffleArray(array) {
  const shuffled = [...array];

  for (let i = shuffled.length - 1; i > 0; i--) {
    const randomIndex = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[i]];
  }

  return shuffled;
}
export const truncateFileName = (
  filename,
  maxLength = 20,
  ellipsisPosition
) => {
  if (filename.length <= maxLength) return filename;

  // Jika ingin ellipsis di tengah
  if (ellipsisPosition === "middle") {
    const ellipsis = "...";
    const availableLength = maxLength - ellipsis.length;
    const halfLength = Math.floor(availableLength / 2);
    const start = filename.substring(0, halfLength);
    const end = filename.substring(
      filename.length - (availableLength - halfLength)
    );
    return start + ellipsis + end;
  }

  // Implementasi asli Anda (ellipsis sebelum ekstensi)
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) {
    return filename.substring(0, maxLength - 3) + "...";
  }

  const name = filename.substring(0, lastDotIndex);
  const extension = filename.substring(lastDotIndex);
  const availableLength = maxLength - extension.length - 3;

  if (availableLength <= 0) {
    return "..." + extension;
  }

  return name.substring(0, availableLength) + "..." + extension;
};

export const getPriorityConfig = (id) => {
  if (id === 1) {
    return { color: "#28a745", dot: "#28a745" };
  }
  if (id === 2) {
    return { color: "#ffc107", dot: "#ffc107" };
  }
  if (id === 3) {
    return { color: "#fd7e14", dot: "#fd7e14" };
  }
  if (id === 4) {
    return { color: "#dc3545", dot: "#dc3545" };
  }
  return { color: "#8C8C8C", dot: "#8C8C8C" };
};

// Deteksi OS mobile dari userAgent (bukan lebar layar).
// Return: "android" | "ios" | "unknown"
export const getMobileOperatingSystem = () => {
  const userAgent =
    navigator.userAgent || navigator.vendor || window.opera || "";

  if (/android/i.test(userAgent)) {
    return "android";
  }

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return "ios";
  }

  return "unknown";
};
